// ============================================
// CONFIGURACIÓN DE ROLES Y PERMISOS
// ============================================

/**
 * Roles disponibles en el sistema
 * Deben coincidir con los del backend (RoleTambo enum)
 */
export const ROLES = {
  DUENIO: "duenio",
  VETERINARIO: "veterinario",
  TAMBERO: "tambero",
  INGENIERO: "ingeniero",
  OBSERVADOR: "observador",
  PENDIENTE: "pendiente",
  ADMINISTRADOR_RURAL: "administrador_rural",
};

/**
 * Permisos disponibles en el sistema
 * Formato: modulo.accion
 */
export const PERMISOS = {
  // Animales
  ANIMALES_LEER: "animales.leer",
  ANIMALES_CREAR: "animales.crear",
  ANIMALES_EDITAR: "animales.editar",
  ANIMALES_ELIMINAR: "animales.eliminar",

  // Lotes
  LOTES_LEER: "lotes.leer",
  LOTES_CREAR: "lotes.crear",
  LOTES_EDITAR: "lotes.editar",
  LOTES_ELIMINAR: "lotes.eliminar",

  // Vacunas/Sanidad
  VACUNAS_LEER: "vacunas.leer",
  VACUNAS_CREAR: "vacunas.crear",
  VACUNAS_EDITAR: "vacunas.editar",
  SANIDAD_LEER: "sanidad.leer",
  SANIDAD_CREAR: "sanidad.crear",
  SANIDAD_EDITAR: "sanidad.editar",

  // Órdenes
  ORDENES_LEER: "ordenes.leer",
  ORDENES_CREAR: "ordenes.crear",
  ORDENES_EDITAR: "ordenes.editar",

  // Tareas
  TAREAS_LEER: "tareas.leer",
  TAREAS_CREAR: "tareas.crear",
  TAREAS_EDITAR: "tareas.editar",

  // Reportes
  REPORTES_LEER: "reportes.leer",

  // Despachos
  DESPACHOS_LEER: "despachos.leer",
  DESPACHOS_CREAR: "despachos.crear",
  DESPACHOS_EDITAR: "despachos.editar",

  // Empresas
  EMPRESAS_LEER: "empresas.leer",
  EMPRESAS_CREAR: "empresas.crear",
  EMPRESAS_EDITAR: "empresas.editar",

  // Dietas
  DIETAS_LEER: "dietas.leer",
  DIETAS_CREAR: "dietas.crear",
  DIETAS_EDITAR: "dietas.editar",

  // Siembras
  SIEMBRAS_LEER: "siembras.leer",
  SIEMBRAS_CREAR: "siembras.crear",
  SIEMBRAS_EDITAR: "siembras.editar",

  // Cosechas
  COSECHAS_LEER: "cosechas.leer",
  COSECHAS_CREAR: "cosechas.crear",
  COSECHAS_EDITAR: "cosechas.editar",

  // Picados
  PICADOS_LEER: "picados.leer",
  PICADOS_CREAR: "picados.crear",
  PICADOS_EDITAR: "picados.editar",

  // Balanceado
  BALANCEADO_LEER: "balanceado.leer",
  BALANCEADO_CREAR: "balanceado.crear",
  BALANCEADO_EDITAR: "balanceado.editar",

  // Maquinarias
  MAQUINARIAS_LEER: "maquinarias.leer",
  MAQUINARIAS_CREAR: "maquinarias.crear",
  MAQUINARIAS_EDITAR: "maquinarias.editar",

  // Estadísticas
  ESTADISTICAS_LEER: "estadisticas.leer",

  // Empleados (solo dueño)
  EMPLEADOS_LEER: "empleados.leer",
  EMPLEADOS_CREAR: "empleados.crear",
  EMPLEADOS_EDITAR: "empleados.editar",
  EMPLEADOS_ELIMINAR: "empleados.eliminar",

  // Ventas
  VENTAS_LEER: "ventas.leer",
  VENTAS_CREAR: "ventas.crear",
  VENTAS_EDITAR: "ventas.editar",
  VENTAS_ELIMINAR: "ventas.eliminar",

  // Materiales
  MATERIALES_LEER: "materiales.leer",
  MATERIALES_CREAR: "materiales.crear",
  MATERIALES_EDITAR: "materiales.editar",
  MATERIALES_ELIMINAR: "materiales.eliminar",

  // Insumos
  INSUMOS_LEER: "insumos.leer",
  INSUMOS_CREAR: "insumos.crear",
  INSUMOS_EDITAR: "insumos.editar",
  INSUMOS_ELIMINAR: "insumos.eliminar",

  // Dietas eliminar
  DIETAS_ELIMINAR: "dietas.eliminar",

  // Cría: reproducción, pesajes y campañas reproductivas
  // (espejo de app/services/permisos_rol.py del backend)
  REPRODUCCION_REGISTRAR: "reproduccion.registrar",
  PESAJES_REGISTRAR: "pesajes.registrar",
  CRIA_CAMPANIAS_LEER: "cria_campanias.leer",
  CRIA_CAMPANIAS_EDITAR: "cria_campanias.editar",
  CRIA_CAMPANIAS_CERRAR: "cria_campanias.cerrar",

  // Gestión (solo dueño)
  GESTION_PARTICIPANTES: "gestion.participantes",
  GESTION_TAMBOS: "gestion.tambos",
};

/**
 * Permisos por rol - Replica la lógica del backend
 * El dueño tiene "*" que significa todos los permisos
 */
export const PERMISOS_POR_ROL = {
  [ROLES.DUENIO]: new Set(["*"]),

  [ROLES.VETERINARIO]: new Set([
    PERMISOS.VACUNAS_LEER,
    PERMISOS.VACUNAS_CREAR,
    PERMISOS.VACUNAS_EDITAR,
    PERMISOS.ANIMALES_LEER,
    PERMISOS.ANIMALES_EDITAR,
    PERMISOS.SANIDAD_LEER,
    PERMISOS.SANIDAD_CREAR,
    PERMISOS.SANIDAD_EDITAR,
    PERMISOS.LOTES_LEER,
    PERMISOS.DIETAS_LEER,
    PERMISOS.TAREAS_LEER,
    PERMISOS.TAREAS_CREAR,
    PERMISOS.TAREAS_EDITAR,
    PERMISOS.REPRODUCCION_REGISTRAR,
    PERMISOS.PESAJES_REGISTRAR,
    PERMISOS.CRIA_CAMPANIAS_LEER,
    PERMISOS.CRIA_CAMPANIAS_EDITAR,
  ]),

  [ROLES.TAMBERO]: new Set([
    PERMISOS.ANIMALES_LEER,
    PERMISOS.ANIMALES_CREAR,
    PERMISOS.ANIMALES_EDITAR,
    PERMISOS.LOTES_LEER,
    PERMISOS.LOTES_EDITAR,
    PERMISOS.TAREAS_LEER,
    PERMISOS.TAREAS_CREAR,
    PERMISOS.TAREAS_EDITAR,
    // Patio de Comidas y Dietas (lectura)
    PERMISOS.BALANCEADO_LEER,
    PERMISOS.DIETAS_LEER,
    // Puede solicitar pedidos de compra por bajo stock
    PERMISOS.ORDENES_CREAR,
    PERMISOS.REPRODUCCION_REGISTRAR,
    PERMISOS.PESAJES_REGISTRAR,
    PERMISOS.CRIA_CAMPANIAS_LEER,
  ]),

  [ROLES.INGENIERO]: new Set([
    PERMISOS.SIEMBRAS_LEER,
    PERMISOS.SIEMBRAS_CREAR,
    PERMISOS.SIEMBRAS_EDITAR,
    PERMISOS.COSECHAS_LEER,
    PERMISOS.COSECHAS_CREAR,
    PERMISOS.COSECHAS_EDITAR,
    PERMISOS.PICADOS_LEER,
    PERMISOS.PICADOS_CREAR,
    PERMISOS.PICADOS_EDITAR,
    PERMISOS.DIETAS_LEER,
    PERMISOS.DIETAS_CREAR,
    PERMISOS.DIETAS_EDITAR,
    PERMISOS.BALANCEADO_LEER,
    PERMISOS.BALANCEADO_CREAR,
    PERMISOS.BALANCEADO_EDITAR,
    PERMISOS.MAQUINARIAS_LEER,
    PERMISOS.MAQUINARIAS_CREAR,
    PERMISOS.MAQUINARIAS_EDITAR,
    PERMISOS.TAREAS_LEER,
    PERMISOS.TAREAS_CREAR,
    PERMISOS.TAREAS_EDITAR,
    PERMISOS.ESTADISTICAS_LEER,
    PERMISOS.CRIA_CAMPANIAS_LEER,
    PERMISOS.CRIA_CAMPANIAS_EDITAR,
  ]),

  [ROLES.OBSERVADOR]: new Set([
    PERMISOS.ANIMALES_LEER,
    PERMISOS.LOTES_LEER,
    PERMISOS.TAREAS_LEER,
    PERMISOS.TAREAS_EDITAR,
    PERMISOS.REPORTES_LEER,
    PERMISOS.ESTADISTICAS_LEER,
    PERMISOS.CRIA_CAMPANIAS_LEER,
  ]),

  [ROLES.ADMINISTRADOR_RURAL]: new Set([
    PERMISOS.SIEMBRAS_LEER,
    PERMISOS.SIEMBRAS_CREAR,
    PERMISOS.SIEMBRAS_EDITAR,
    PERMISOS.COSECHAS_LEER,
    PERMISOS.COSECHAS_CREAR,
    PERMISOS.COSECHAS_EDITAR,
    PERMISOS.PICADOS_LEER,
    PERMISOS.PICADOS_CREAR,
    PERMISOS.PICADOS_EDITAR,
    PERMISOS.DIETAS_LEER,
    PERMISOS.DIETAS_CREAR,
    PERMISOS.DIETAS_EDITAR,
    PERMISOS.BALANCEADO_LEER,
    PERMISOS.BALANCEADO_CREAR,
    PERMISOS.BALANCEADO_EDITAR,
    PERMISOS.MAQUINARIAS_LEER,
    PERMISOS.MAQUINARIAS_CREAR,
    PERMISOS.MAQUINARIAS_EDITAR,
    PERMISOS.TAREAS_LEER,
    PERMISOS.TAREAS_CREAR,
    PERMISOS.TAREAS_EDITAR,
    PERMISOS.ESTADISTICAS_LEER,
    PERMISOS.ORDENES_LEER,
    PERMISOS.ORDENES_CREAR,
    PERMISOS.ORDENES_EDITAR,
    // Sin VENTAS (no hay venta de animales en modo agrícola)
  ]),

  [ROLES.PENDIENTE]: new Set([]),
};

/**
 * Mapeo de rutas a permisos requeridos
 * Puedes modificar esto para definir qué permisos necesita cada ruta
 */
export const RUTAS_PERMISOS = {
  // Rutas de gestión (solo dueño)
  tambos: [], // Todos pueden ver sus tambos
  participantes: [PERMISOS.GESTION_PARTICIPANTES],

  // Rutas de animales
  vacas: [PERMISOS.ANIMALES_LEER],
  lotes: [PERMISOS.LOTES_LEER],

  // Rutas de sanidad
  veterinario: [PERMISOS.VACUNAS_LEER, PERMISOS.SANIDAD_LEER],

  // Rutas de producción
  despachos: [PERMISOS.DESPACHOS_LEER],
  empresas: [PERMISOS.EMPRESAS_LEER],
  dietas: [PERMISOS.DIETAS_LEER],
  siembras: [PERMISOS.SIEMBRAS_LEER],
  cosechas: [PERMISOS.COSECHAS_LEER],
  picados: [PERMISOS.PICADOS_LEER],
  ventaAgro: [PERMISOS.PICADOS_LEER],
  patioComidas: [PERMISOS.BALANCEADO_LEER],
  maquinarias: [PERMISOS.MAQUINARIAS_LEER],
  ordenesCompra: [PERMISOS.ORDENES_LEER],
  tareas: [PERMISOS.TAREAS_LEER],
  gastosTercerizados: [PERMISOS.ORDENES_LEER, PERMISOS.TAREAS_LEER],
  pasturas: [PERMISOS.SIEMBRAS_LEER, PERMISOS.LOTES_LEER],
  forrajes: [
    PERMISOS.PICADOS_LEER,
    PERMISOS.BALANCEADO_LEER,
    PERMISOS.SIEMBRAS_LEER,
  ],

  ventas: [PERMISOS.VENTAS_LEER],
  materiales: [PERMISOS.MATERIALES_LEER],
  insumos: [PERMISOS.INSUMOS_LEER],

  // Rutas de reportes
  estadisticas: [PERMISOS.ESTADISTICAS_LEER, PERMISOS.REPORTES_LEER],

  // Rutas de gestión financiera / administrativa (solo dueño/gestor)
  facturacion: [PERMISOS.GESTION_PARTICIPANTES], // Usa permiso de gestión (solo DUENIO)
  cheques: [PERMISOS.GESTION_PARTICIPANTES],
  auditoria: [PERMISOS.GESTION_PARTICIPANTES],
  erp: [PERMISOS.GESTION_PARTICIPANTES],
  comprobantesERP: [PERMISOS.GESTION_PARTICIPANTES],
  libroIva: [PERMISOS.GESTION_PARTICIPANTES],
  retenciones: [PERMISOS.GESTION_PARTICIPANTES],
  tramitesSenasa: [PERMISOS.GESTION_PARTICIPANTES],

  // Rutas de eficiencia reproductiva de cría
  criaEficiencia: [PERMISOS.CRIA_CAMPANIAS_LEER],
  criaCampania: [PERMISOS.CRIA_CAMPANIAS_LEER],
  criaFichaEficiencia: [PERMISOS.CRIA_CAMPANIAS_LEER],
  criaRankings: [PERMISOS.CRIA_CAMPANIAS_LEER],
  criaComparacion: [PERMISOS.CRIA_CAMPANIAS_LEER],
  criaCalidad: [PERMISOS.REPRODUCCION_REGISTRAR],

  // Rutas Feedlot
  corralesFeedlot: [PERMISOS.LOTES_LEER],
  engorde: [PERMISOS.ANIMALES_LEER],
  compraAnimales: [PERMISOS.ANIMALES_LEER, PERMISOS.ORDENES_LEER],
};

/**
 * Descripción de roles para mostrar en UI
 */
export const ROLES_INFO = {
  [ROLES.DUENIO]: {
    nombre: "Dueño/Gestor",
    descripcion:
      "Control total del tambo. Puede gestionar todo y asignar roles.",
    color: "success",
    icono: "bi-shield-fill-check",
  },
  [ROLES.VETERINARIO]: {
    nombre: "Veterinario",
    descripcion:
      "Gestión de sanidad animal: vacunas, tratamientos, historial médico.",
    color: "info",
    icono: "bi-heart-pulse-fill",
  },
  [ROLES.TAMBERO]: {
    nombre: "Tambero",
    descripcion:
      "Gestión operativa: animales, lotes, dietas, patio de comidas, tareas y solicitudes de compra.",
    color: "primary",
    icono: "bi-gear-fill",
  },
  [ROLES.INGENIERO]: {
    nombre: "Ingeniero Agrónomo",
    descripcion:
      "Gestión agronómica: siembras, cosechas, dietas, balanceado, maquinarias.",
    color: "success",
    icono: "bi-tree-fill",
  },
  [ROLES.OBSERVADOR]: {
    nombre: "Observador",
    descripcion:
      "Solo lectura de todo + tareas. Puede ver información y gestionar tareas asignadas.",
    color: "secondary",
    icono: "bi-eye-fill",
  },
  [ROLES.ADMINISTRADOR_RURAL]: {
    nombre: "Administrador Rural",
    descripcion:
      "Gestión agrícola completa: siembras, cosechas, picados, maquinarias, compras y estadísticas. Sin acceso a venta de animales.",
    color: "success",
    icono: "bi-clipboard2-data-fill",
  },
  [ROLES.PENDIENTE]: {
    nombre: "Pendiente",
    descripcion: "Usuario invitado sin permisos asignados aún.",
    color: "warning",
    icono: "bi-hourglass-split",
  },
};

/**
 * Verifica si un rol tiene un permiso específico
 * @param {string} role - El rol del usuario
 * @param {string} permiso - El permiso a verificar
 * @param {object} permisosPersonalizados - Permisos adicionales del usuario
 * @returns {boolean}
 */
export function tienePermiso(role, permiso, permisosPersonalizados = {}) {
  // Usuario pendiente no tiene permisos
  if (role === ROLES.PENDIENTE) {
    return false;
  }

  // Dueño tiene todos los permisos
  if (role === ROLES.DUENIO) {
    return true;
  }

  // 1. Verificar si hay un permiso personalizado explícito (True o False)
  if (permisosPersonalizados && permisosPersonalizados[permiso] !== undefined) {
    return !!permisosPersonalizados[permiso];
  }

  // 2. Verificar permisos del rol
  const permisosRol = PERMISOS_POR_ROL[role] || new Set();
  if (permisosRol.has("*") || permisosRol.has(permiso)) {
    return true;
  }

  return false;
}

/**
 * Catálogo canónico de vistas del sistema configurables para empleados
 */
export const VISTAS_CONFIGURABLES = [
  // Rodeo & Animales
  {
    key: "vacas",
    label: "Animales y Grilla",
    categoria: "Rodeo",
    icono: "bi-card-list",
    permisos: [PERMISOS.ANIMALES_LEER],
  },
  {
    key: "lotes",
    label: "Lotes de Vacas",
    categoria: "Rodeo",
    icono: "bi-collection",
    permisos: [PERMISOS.LOTES_LEER],
  },
  {
    key: "veterinario",
    label: "Sanidad e Inventario Vet.",
    categoria: "Rodeo",
    icono: "bi-heart-pulse",
    permisos: [PERMISOS.VACUNAS_LEER, PERMISOS.SANIDAD_LEER],
  },
  {
    key: "rodeoCria",
    label: "Rodeo de Cría",
    categoria: "Rodeo",
    icono: "bi-shield-shaded",
    permisos: [PERMISOS.ANIMALES_LEER, PERMISOS.LOTES_LEER],
  },

  // Actividad Lechera
  {
    key: "despachos",
    label: "Despachos de Leche",
    categoria: "Lechería",
    icono: "bi-truck",
    permisos: [PERMISOS.DESPACHOS_LEER],
  },
  {
    key: "empresas",
    label: "Empresas Lácteas",
    categoria: "Lechería",
    icono: "bi-building",
    permisos: [PERMISOS.EMPRESAS_LEER],
  },

  // Feedlot & Engorde
  {
    key: "corralesFeedlot",
    label: "Corrales y Potreros",
    categoria: "Feedlot",
    icono: "bi-grid-3x3",
    permisos: [PERMISOS.LOTES_LEER],
  },
  {
    key: "engorde",
    label: "Engorde a Corral",
    categoria: "Feedlot",
    icono: "bi-activity",
    permisos: [PERMISOS.ANIMALES_LEER],
  },
  {
    key: "compraAnimales",
    label: "Compra de Animales",
    categoria: "Feedlot",
    icono: "bi-cart-plus",
    permisos: [PERMISOS.ANIMALES_LEER, PERMISOS.ORDENES_LEER],
  },

  // Alimentación
  {
    key: "dietas",
    label: "Dietas",
    categoria: "Alimentación",
    icono: "bi-clipboard2-heart",
    permisos: [PERMISOS.DIETAS_LEER],
  },
  {
    key: "patioComidas",
    label: "Patio de Comidas",
    categoria: "Alimentación",
    icono: "bi-box-seam",
    permisos: [PERMISOS.BALANCEADO_LEER],
  },

  // Agricultura
  {
    key: "mapaAgronomico",
    label: "Mapa Agronómico",
    categoria: "Agricultura",
    icono: "bi-globe-americas",
    permisos: [PERMISOS.SIEMBRAS_LEER, PERMISOS.LOTES_LEER],
  },
  {
    key: "pasturas",
    label: "Pasturas y Verdeos",
    categoria: "Agricultura",
    icono: "bi-flower3",
    permisos: [PERMISOS.SIEMBRAS_LEER, PERMISOS.LOTES_LEER],
  },
  {
    key: "forrajes",
    label: "Forrajes",
    categoria: "Agricultura",
    icono: "bi-flower2",
    permisos: [
      PERMISOS.PICADOS_LEER,
      PERMISOS.BALANCEADO_LEER,
      PERMISOS.SIEMBRAS_LEER,
    ],
  },
  {
    key: "siembras",
    label: "Siembras",
    categoria: "Agricultura",
    icono: "bi-flower1",
    permisos: [PERMISOS.SIEMBRAS_LEER],
  },
  {
    key: "cosechas",
    label: "Cosechas",
    categoria: "Agricultura",
    icono: "bi-flower2",
    permisos: [PERMISOS.COSECHAS_LEER],
  },
  {
    key: "picados",
    label: "Picados",
    categoria: "Agricultura",
    icono: "bi-scissors",
    permisos: [PERMISOS.PICADOS_LEER],
  },
  {
    key: "insumos",
    label: "Insumos Agrícolas",
    categoria: "Agricultura",
    icono: "bi-archive",
    permisos: [PERMISOS.INSUMOS_LEER],
  },
  {
    key: "maquinarias",
    label: "Maquinaria y Cisterna",
    categoria: "Agricultura",
    icono: "bi-gear",
    permisos: [PERMISOS.MAQUINARIAS_LEER],
  },
  {
    key: "ventaAgro",
    label: "Venta Agrícola",
    categoria: "Agricultura",
    icono: "bi-cash-coin",
    permisos: [PERMISOS.PICADOS_LEER],
  },

  // Operaciones & Gestión
  {
    key: "tareas",
    label: "Tareas y Pendientes",
    categoria: "Operaciones",
    icono: "bi-check2-square",
    permisos: [PERMISOS.TAREAS_LEER],
  },
  {
    key: "ordenesCompra",
    label: "Órdenes de Compra",
    categoria: "Operaciones",
    icono: "bi-receipt",
    permisos: [PERMISOS.ORDENES_LEER],
  },
  {
    key: "gastosTercerizados",
    label: "Gastos Tercerizados",
    categoria: "Operaciones",
    icono: "bi-wallet2",
    permisos: [PERMISOS.ORDENES_LEER, PERMISOS.TAREAS_LEER],
  },
  {
    key: "ventas",
    label: "Venta de Animales",
    categoria: "Operaciones",
    icono: "bi-cash-stack",
    permisos: [PERMISOS.VENTAS_LEER],
  },
  {
    key: "estadisticas",
    label: "Estadísticas",
    categoria: "Reportes",
    icono: "bi-bar-chart",
    permisos: [PERMISOS.ESTADISTICAS_LEER, PERMISOS.REPORTES_LEER],
  },
];

/**
 * Obtiene el mapa de vistas por defecto para un rol dado
 * @param {string} role
 * @returns {Record<string, boolean>}
 */
export function getVistasPorDefecto(role) {
  const result = {};
  if (!role || role === ROLES.PENDIENTE) {
    VISTAS_CONFIGURABLES.forEach((v) => {
      result[v.key] = false;
    });
    return result;
  }
  if (role === ROLES.DUENIO) {
    VISTAS_CONFIGURABLES.forEach((v) => {
      result[v.key] = true;
    });
    return result;
  }
  const permisosRol = PERMISOS_POR_ROL[role] || new Set();
  VISTAS_CONFIGURABLES.forEach((vista) => {
    const tiene = vista.permisos.some(
      (p) => permisosRol.has(p) || permisosRol.has("*")
    );
    result[vista.key] = tiene;
  });
  return result;
}

/**
 * Obtiene la lista de vistas con su estado resuelto (habilitada, porDefecto, personalizada)
 * @param {string} role
 * @param {object} permisosPersonalizados
 * @returns {Array}
 */
export function getVistasConEstado(role, permisosPersonalizados = {}) {
  const defaultMap = getVistasPorDefecto(role);
  const vistasCustom =
    (permisosPersonalizados && permisosPersonalizados.vistas) || {};

  return VISTAS_CONFIGURABLES.map((v) => {
    const isOverridden = vistasCustom[v.key] !== undefined;
    const habilitada = isOverridden ? !!vistasCustom[v.key] : defaultMap[v.key];
    return {
      ...v,
      habilitada,
      porDefecto: defaultMap[v.key],
      personalizada: isOverridden && vistasCustom[v.key] !== defaultMap[v.key],
    };
  });
}

/**
 * Verifica si un rol es gestor (dueño)
 * @param {string} role
 * @returns {boolean}
 */
export function esGestor(role) {
  return role === ROLES.DUENIO;
}

/**
 * Verifica si un rol es empleado (cualquier rol que no sea dueño)
 * @param {string} role
 * @returns {boolean}
 */
export function esEmpleado(role) {
  return role && role !== ROLES.DUENIO && role !== ROLES.PENDIENTE;
}

/**
 * Obtiene los roles que puede asignar un gestor
 * @returns {Array}
 */
export function getRolesAsignables() {
  return [
    ROLES.VETERINARIO,
    ROLES.TAMBERO,
    ROLES.INGENIERO,
    ROLES.OBSERVADOR,
    ROLES.ADMINISTRADOR_RURAL,
  ];
}
